/**
 * WeekSelector Component
 * Allows users to select a week reference type (recurring or specific week)
 */
import React, { useState } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Repeat as RepeatIcon,
  CalendarMonth as CalendarIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  WeekReference,
  WeekReferenceType,
  getWeekStartDate,
  formatWeekRange,
} from '../../types/schedule';

interface WeekSelectorProps {
  value: WeekReference;
  onChange: (value: WeekReference) => void;
  disabled?: boolean;
}

export const WeekSelector: React.FC<WeekSelectorProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date>(() => {
    if (value.type === 'specific' && value.weekStartDate) {
      return new Date(value.weekStartDate);
    }
    return getWeekStartDate(new Date());
  });

  const handleTypeChange = (type: WeekReferenceType) => {
    if (type === 'recurring') {
      onChange({ type: 'recurring', weekStartDate: null });
    } else {
      const weekStart = getWeekStartDate(new Date());
      onChange({ type: 'specific', weekStartDate: weekStart.getTime() });
      setSelectedDate(weekStart);
    }
  };

  const handleWeekChange = (direction: 'prev' | 'next') => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
    setSelectedDate(newDate);
  };

  const handleConfirmWeek = () => {
    const weekStart = getWeekStartDate(selectedDate);
    onChange({ type: 'specific', weekStartDate: weekStart.getTime() });
    setDialogOpen(false);
  };

  const handleGoToCurrentWeek = () => {
    const currentWeekStart = getWeekStartDate(new Date());
    setSelectedDate(currentWeekStart);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <Typography variant="subtitle2" color="text.secondary">
          {t('schedules.weekly.weekReference')}:
        </Typography>
        <ButtonGroup size="small" disabled={disabled}>
          <Button
            variant={value.type === 'recurring' ? 'contained' : 'outlined'}
            onClick={() => handleTypeChange('recurring')}
            startIcon={<RepeatIcon />}
          >
            {t('schedules.weekly.recurring')}
          </Button>
          <Button
            variant={value.type === 'specific' ? 'contained' : 'outlined'}
            onClick={() => handleTypeChange('specific')}
            startIcon={<CalendarIcon />}
          >
            {t('schedules.weekly.specific')}
          </Button>
        </ButtonGroup>
      </Box>

      {value.type === 'specific' && value.weekStartDate && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('schedules.weekly.weekOf', { date: formatWeekRange(value.weekStartDate) })}
          </Typography>
          <Button
            size="small"
            onClick={() => setDialogOpen(true)}
            disabled={disabled}
          >
            {t('schedules.weekly.changeWeek')}
          </Button>
        </Box>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('schedules.weekly.selectWeek')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, py: 2 }}>
            <IconButton onClick={() => handleWeekChange('prev')}>
              <ChevronLeftIcon />
            </IconButton>
            <Typography variant="h6" sx={{ minWidth: 200, textAlign: 'center' }}>
              {formatWeekRange(selectedDate.getTime())}
            </Typography>
            <IconButton onClick={() => handleWeekChange('next')}>
              <ChevronRightIcon />
            </IconButton>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button size="small" onClick={handleGoToCurrentWeek}>
              Go to Current Week
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleConfirmWeek}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WeekSelector;
